import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { ErrorState, Loading } from "../ui/State";
type Option = {
  label: string;
  decision: string;
  annualCost: string | null;
  vatBasis: string;
  terms: string;
};
type Review = {
  id: string;
  aggregate_id: string;
  status: string;
  summary: string;
  version: number;
  context: {
    options: Option[];
    selected: number;
    rationale: string;
    actionId?: string;
  };
};
type Contract = {
  id: string;
  title: string;
  annual_cost: string | null;
  vat_basis: string;
  notice_deadline: string | null;
  renewal_on: string | null;
  ends_on: string | null;
};
type Snapshot = {
  contract: Contract;
  sourceHash: string;
  coverage: string;
  truncated: boolean;
  work: {
    id: string;
    work_number: string;
    title: string;
    lifecycle_status: string;
    estimated_cost: string | null;
    actual_cost: string | null;
    defects: number;
  }[];
};
export function ContractRenewalsAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building") ?? "",
    scope = "?building=" + encodeURIComponent(building),
    [model, setModel] = useState<{
      contracts: Contract[];
      reviews: Review[];
    }>(),
    [selected, setSelected] = useState<Snapshot>(),
    [error, setError] = useState<unknown>();
  const load = () =>
    api<{ contracts: Contract[]; reviews: Review[] }>(
      "/api/alpha/contract-renewals" + scope,
    ).then(setModel);
  useEffect(() => {
    setSelected(undefined);
    setModel(undefined);
    if (building) void load().catch(setError);
  }, [building]);
  if (!building)
    return (
      <p>
        Choose a building in <Link to="/alpha">Feature previews</Link>.
      </p>
    );
  return (
    <>
      <h1>Service-contract renewal decisions</h1>
      <p>
        Compare renewal choices and service history before an authorised
        decision. Approved instructions create an Action for staff; no notice is
        sent or new contract appointed here.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {!model ? (
        <Loading />
      ) : (
        <>
          <h2>Contracts</h2>
          <p>
            Showing up to 200 contracts, earliest notice deadline first. A
            missing date needs review.
          </p>
          {model.contracts.map((c) => (
            <section className="panel" key={c.id}>
              <h3>{c.title}</h3>
              <p>
                Annual cost: {c.annual_cost ?? "Not recorded"} · {c.vat_basis}
              </p>
              <p>
                Notice deadline: {c.notice_deadline ?? "Not recorded"} ·
                Renewal: {c.renewal_on ?? "Not recorded"} · End:{" "}
                {c.ends_on ?? "Not recorded"}
              </p>
              <button
                onClick={() =>
                  void api<Snapshot>(
                    "/api/alpha/contract-renewals/" + c.id + scope,
                  )
                    .then(setSelected)
                    .catch(setError)
                }
              >
                Compare options for {c.title}
              </button>
            </section>
          ))}
          {!model.contracts.length && (
            <p>No service contracts recorded for this building.</p>
          )}
          {selected && (
            <Prepare
              key={selected.contract.id + selected.sourceHash}
              snapshot={selected}
              scope={scope}
              done={async () => {
                setSelected(undefined);
                await load();
              }}
            />
          )}
          <h2>Decision history</h2>
          {model.reviews.map((r) => (
            <section className="panel" key={r.id}>
              <h3>
                {r.summary} · {r.status}
              </h3>
              <p>{r.context.rationale}</p>
              {r.context.options.map((o, i) => (
                <p key={i}>
                  {i === r.context.selected ? "Recommended: " : ""}
                  {o.label} · {o.decision} · Annual cost{" "}
                  {o.annualCost ?? "Unknown"} · {o.vatBasis}. {o.terms}
                </p>
              ))}
              {r.context.actionId && (
                <Link to={"/work/actions/" + r.context.actionId}>
                  Open approved follow-up Action
                </Link>
              )}
              {r.status === "Pending" && (
                <Decide review={r} scope={scope} done={load} />
              )}
            </section>
          ))}
        </>
      )}
    </>
  );
}
function Prepare({
  snapshot: s,
  scope,
  done,
}: {
  snapshot: Snapshot;
  scope: string;
  done: () => Promise<void>;
}) {
  const [count, setCount] = useState(2),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>();
  const pending = useRef<object | null>(null);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    pending.current ??= {
      sourceHash: s.sourceHash,
      options: Array.from({ length: count }, (_, i) => ({
        label: f.get("label" + i),
        decision: f.get("decision" + i),
        annualCost: f.get("cost" + i) || null,
        vatBasis: f.get("vat" + i),
        terms: f.get("terms" + i),
      })),
      selected: Number(f.get("selected")),
      reason: f.get("reason"),
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      await alphaSave(
        pending,
        "/api/alpha/contract-renewals/" + s.contract.id + "/prepare" + scope,
        { method: "POST", body: JSON.stringify(pending.current) },
      );
      pending.current = null;
      await done();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel">
      <h2>Compare {s.contract.title}</h2>
      <p>{s.coverage}</p>
      {s.work.map((w) => (
        <p key={w.id}>
          <Link to={"/work/" + w.id}>
            {w.work_number}: {w.title}
          </Link>{" "}
          · {w.lifecycle_status} · Estimate {w.estimated_cost ?? "Unknown"},
          actual {w.actual_cost ?? "Unknown"}, recorded defect inspections{" "}
          {w.defects}
        </p>
      ))}
      {s.truncated && (
        <p>More than 100 work records: review the full Work register.</p>
      )}
      {Boolean(error) && <ErrorState error={error} />}
      <form onSubmit={save}>
        <fieldset disabled={busy || !!pending.current}>
          <legend>Renewal options</legend>
          {Array.from({ length: count }, (_, i) => (
            <section key={i}>
              <h3>Option {i + 1}</h3>
              <label>
                Option name
                <input
                  name={"label" + i}
                  required
                  minLength={3}
                  maxLength={200}
                />
              </label>
              <label>
                Outcome
                <select name={"decision" + i}>
                  <option>Continue</option>
                  <option>Retender</option>
                  <option>Terminate</option>
                </select>
              </label>
              <label>
                Annual cost (£), leave blank if unknown
                <input
                  name={"cost" + i}
                  inputMode="decimal"
                  pattern="[0-9]+(\.[0-9]{1,2})?"
                />
              </label>
              <label>
                VAT basis
                <select name={"vat" + i}>
                  <option>Unknown</option>
                  <option>Excluding VAT</option>
                  <option>Including VAT</option>
                  <option>No VAT</option>
                </select>
              </label>
              <label>
                Terms and proposed instruction
                <textarea
                  name={"terms" + i}
                  required
                  minLength={5}
                  maxLength={3000}
                />
              </label>
            </section>
          ))}
          {count < 10 && (
            <button type="button" onClick={() => setCount(count + 1)}>
              Add another option
            </button>
          )}
          <label>
            Recommended option
            <select name="selected">
              {Array.from({ length: count }, (_, i) => (
                <option value={i} key={i}>
                  {i + 1}
                </option>
              ))}
            </select>
          </label>
          <label>
            Recommendation and contract review
            <textarea name="reason" maxLength={3000} />
          </label>
        </fieldset>
        <button disabled={busy}>
          {pending.current ? "Retry same proposal" : "Submit for approval"}
        </button>
      </form>
    </section>
  );
}
function Decide({
  review: r,
  scope,
  done,
}: {
  review: Review;
  scope: string;
  done: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>();
  const pending = useRef<object | null>(null);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    pending.current ??= {
      decision: f.get("decision"),
      reason: f.get("reason"),
      expectedVersion: r.version,
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      await alphaSave(
        pending,
        "/api/alpha/contract-renewals/reviews/" + r.id + "/decide" + scope,
        { method: "POST", body: JSON.stringify(pending.current) },
      );
      pending.current = null;
      await done();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={save}>
      {Boolean(error) && <ErrorState error={error} />}
      <p>
        An authorised reviewer must decide; the configured controls policy
        determines whether a different person is required. Approval records the
        selected option and creates its follow-up Action.
      </p>
      <fieldset disabled={busy || !!pending.current}>
        <label>
          Decision
          <select name="decision">
            <option>Approved</option>
            <option>Returned</option>
            <option>Rejected</option>
          </select>
        </label>
        <label>
          Decision reason
          <textarea name="reason" maxLength={3000} />
        </label>
      </fieldset>
      <button disabled={busy}>
        {pending.current ? "Retry same decision" : "Record decision"}
      </button>
    </form>
  );
}
